import {Component, inject, signal} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TranslationService} from '../../../core/services/translation.service';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {BaseDataService} from '../../../core/services/base-data.service';
import {ActivatedRoute} from "@angular/router";
import {ConfigService} from "../../../core/services/config.service";
import {HupSubscription} from "../../../data/models/subscription.model";
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {HouseNoPipe} from "../../../core/pipes/houseno.pipe";
import {LandingPageService} from "../../../data/services/landing-page.service";
import {RedirectService} from "../../../data/services/redirect.service";
import {LandingPage} from "../../../data/models/landing-page.model";
import {GeneralErrorsComponent} from "../../../core/components/general-errors/general-errors.component";
import {MatButton} from "@angular/material/button";
import {LoadingSpinnerComponent} from "../../../core/components/loading-spinner/loading-spinner.component";
import {ClientSearchComponent} from "../../client-search/client-search.component";
import {MatError} from "@angular/material/form-field";

import {MatCard, MatCardActions, MatCardContent, MatCardTitle} from "@angular/material/card";
import {ApplyIfNotEmptyDirective} from "../../../core/directives/apply-if-not-empty.directive";
import {isDigital} from "../../../core/utils/offer-utils";

@Component({
  selector: 'app-complaint-wihtout-login',
  templateUrl: './complaint-wihtout-login.component.html',
  styleUrls: ['./complaint-wihtout-login.component.scss'],
  providers: [HouseNoPipe],
  imports: [
    GeneralErrorsComponent,
    MatButton,
    LoadingSpinnerComponent,
    ClientSearchComponent,
    MatError,
    MatCard,
    MatCardTitle,
    MatCardActions,
    MatCardContent,
    ApplyIfNotEmptyDirective
  ],
  standalone: true
})
export class ComplaintWihtoutLoginComponent {
  translationService = inject(TranslationService);
  private formBuilder = inject(FormBuilder);
  private subscriptionService = inject(HupSubscriptionsService);
  private baseDataService = inject(BaseDataService);
  private navigationService = inject(NavigationService);
  private landingPageService = inject(LandingPageService);
  private redirectService = inject(RedirectService);
  private route = inject(ActivatedRoute);
  private configService = inject(ConfigService);
  houseNoPipe = inject(HouseNoPipe);

  errorsArr: string[] = [];
  isMobile: boolean = false;
  clientNoForm: FormGroup;
  orders: HupSubscription[] = [];
  isLoadingComp: boolean = false;
  isLoadingSub: boolean = false;
  isDeactivated: boolean = false;
  noOrdersFound: boolean = false;
  showContactFormOnComplaintViewWithoutLogin: boolean = true;
  useIdentificationForm: boolean = false;
  identified: boolean = false;

  private readonly SHOW_PREVIEW_IF_SINGLE_SUBSCRIPTION = 'complaint.singleSubscription.showPreview';
  private showPreviewIfSingleSubscription = signal(false);

  constructor() {
    this.clientNoForm = this.formBuilder.group({
      clientNo: ['', Validators.required]
    });

    this.showContactFormOnComplaintViewWithoutLogin = this.configService.getConfig('showContactFormOnComplaintViewWithoutLogin')?.asBoolean();

    const showPreviewIfSingleSubscription =  this.configService.getConfig(this.SHOW_PREVIEW_IF_SINGLE_SUBSCRIPTION)?.asBoolean() ?? false;
    this.showPreviewIfSingleSubscription.set(showPreviewIfSingleSubscription);

    this.useIdentificationForm = this.configService.getConfig('withoutLogin.useIdentificationForm')?.asBoolean();

    this.route.paramMap.subscribe((params) => {

      const clientNo : number = Number(params.get('clientNo'));
      if(clientNo) {
        this.identified = true;
        this.loadOrdersForClientno(clientNo);
      }
    });
    const configBegin = this.configService.getConfig('complaint.deactivate.begin')
    if(configBegin){
      const configEnd = this.configService.getConfig('complaint.deactivate.end')
      const begin = configBegin.value.split(',', 3);  // 0,11,30
      const end = configEnd.value.split(',', 3);      // 6,11,30
      this.isDeactivated = this.isTodayBetweenWeekdays(+begin[0], +begin[1], +begin[2], +end[0], +end[1], +end[2]);
    }
  }

  isTodayBetweenWeekdays(
    startDay: number, startHour: number, startMinute: number,
    endDay: number, endHour: number, endMinute: number
  ): boolean {
    const now = new Date();
    const currentDay = now.getDay(); // 0 (Sonntag) bis 6 (Samstag)
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const startMinutes = startDay * 1440 + startHour * 60 + startMinute;
    const endMinutes = endDay * 1440 + endHour * 60 + endMinute;
    const nowMinutes = currentDay * 1440 + currentMinutes;

    return startMinutes <= endMinutes
      ? nowMinutes >= startMinutes && nowMinutes <= endMinutes
      : nowMinutes >= startMinutes || nowMinutes <= endMinutes;
  }

  loadOrdersForClientno(clientNo: number): void {
    this.isLoadingSub = true;
    this.orders = [];
    this.noOrdersFound = false;
    if (clientNo && Number(clientNo) > 0) {
      this.subscriptionService.getSubscriptionsUnauthorized(clientNo).subscribe(orders => {
        this.isLoadingSub = false;

        let validatedOrders: HupSubscription[] = [];
        orders.forEach(order => {
          if (order.validDate.validFrom <= new Date()) {
            validatedOrders.push(order);
          }
        });

        if (validatedOrders.length === 1 && validatedOrders[0] && !this.showPreviewIfSingleSubscription()) {
          this.onNewComplaint(validatedOrders[0]);
        } else if ((validatedOrders.length === 1 && validatedOrders[0] && this.showPreviewIfSingleSubscription()) || validatedOrders.length > 1) {
          validatedOrders.forEach(order => {
            this.baseDataService.handleUnauthorizedOrder(order);
            if(!order.variantDescription) {
              this.baseDataService.loadBaseData('variantcode').subscribe(
                (bds) => {
                  bds?.forEach(bd => {
                    if(bd.key === order.variantCode) {
                      order.variantDescription = bd.description;
                    }
                  })
                }
              );
            }
            this.orders.push(order);
          });
        } else {
          this.noOrdersFound = true;
        }
      });
    }
  }

  onNewComplaint(subscription: HupSubscription): void {
    this.navigationService.navigateTo(NavigationCommand.COMPLAINTWITHOUTLOGINNEW, [subscription.backendId + '', subscription.userBackendId + '', 'true', 'true', (isDigital(subscription.offer?.offerType) || isDigital(subscription.subscriptionType) ? 'false' : 'true')]);
  }

  navigateToContact(): void {
    this.navigateTo(NavigationCommand.CONTACT)
  }

  navigateToIdentification(): void {
    this.navigateTo(NavigationCommand.IDENTIFICATION, [NavigationCommand.COMPLAINT.substring(1)]);
  }

  navigateTo(subPath: NavigationCommand, params: string[] = []) {
    this.landingPageService.getLandingPages().subscribe(pages => {
      let foundPage : LandingPage = null;
      pages.forEach(page => {
        if (page && subPath.endsWith(page.link)) {
          foundPage = page;
        }
      });
      if(foundPage && foundPage.isExtern) {
        this.redirectService.redirect(foundPage.externLink, true);
      } else {
        this.navigationService.navigateTo(subPath, params);
      }
    });
  }

}
